import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Cloudscape popovers position their panel once at open time and do
 * not track the trigger during scroll, so scrolling leaves the panel
 * floating over unrelated content. This hook returns a `key` that
 * flips on the first scroll after `arm()` is called, plus the `arm`
 * function to invoke from the trigger's click handler. Passing `key`
 * to the popover forces a remount on scroll, which closes it.
 *
 * The scroll listener is attached lazily inside `arm()` and torn
 * down on the first scroll (and on unmount), so callers that render
 * many instances of the trigger (e.g. one `CopyButton` per table
 * row) do not each hold a live `window` listener between clicks.
 */
export function useScrollDismissKey(): {
  readonly key: number;
  readonly arm: () => void;
} {
  const [key, setKey] = useState(0);
  const cleanupRef = useRef<(() => void) | undefined>(undefined);

  // Ensure any armed listener is removed on unmount.
  useEffect(() => () => cleanupRef.current?.(), []);

  const arm = useCallback(() => {
    // Repeated clicks without an intervening scroll should not
    // stack multiple listeners — drop any prior one first.
    cleanupRef.current?.();

    const onScroll = () => {
      cleanupRef.current?.();
      cleanupRef.current = undefined;
      setKey((k) => k + 1);
    };
    // capture: true so scrolls inside ancestor scroll containers also
    // fire — a page-level `scroll` listener would miss them when the
    // scrolling ancestor is not `window`.
    window.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    });
    cleanupRef.current = () =>
      window.removeEventListener("scroll", onScroll, true);
  }, []);

  return { key, arm };
}
